/**
 * VRG-734 — VERAfied, the video interview series (GTM channel #8, Content marketing). Guests,
 * episodes and the reusable format segments an episode is built from.
 *
 * Every list here is text + CHECK in migration 0169, not a Postgres enum, for the same reason as
 * 0136/0137: adding a value is one constraint swap. `verafied.guard.spec.ts` compares these lists
 * with the CHECKs, so the two cannot drift.
 *
 * A guest is a ROLE on someone we already store, not a separate list: `verafied_guests.lead_id`
 * points at the person (`sales_leads`) when one exists, and the guest row carries its own name only
 * when nobody matches. See the migration header for why the row cannot always point somewhere.
 */

/** Person, or an organisation/band (the guest list includes Metallica). */
export const VERAFIED_GUEST_TYPES = ['person', 'organisation'] as const;
export type VerafiedGuestType = (typeof VERAFIED_GUEST_TYPES)[number];

/**
 * Guest status, in order. "Ready to invite" is a status rather than a flag, so a guest cannot be
 * both ready and invited (spec §1). `paused` can follow any of them.
 */
export const VERAFIED_GUEST_STATUSES = [
  'not_invited',
  'ready_to_invite',
  'invited',
  'accepted',
  'declined',
  'no_reply',
  'scheduled',
  'recorded',
  'published',
  'paused',
] as const;
export type VerafiedGuestStatus = (typeof VERAFIED_GUEST_STATUSES)[number];

export const VERAFIED_GUEST_STATUS_LABELS: Readonly<Record<VerafiedGuestStatus, string>> = {
  not_invited: 'Not invited',
  ready_to_invite: 'Ready to invite',
  invited: 'Invited',
  accepted: 'Accepted',
  declined: 'Declined',
  no_reply: 'No reply',
  scheduled: 'Scheduled',
  recorded: 'Recorded',
  published: 'Published',
  paused: 'Paused',
};

/**
 * Statuses a guest with an open check (`check_required`) may still hold. Anything past these is an
 * approach to the guest, which the check exists to stop: Rudy Kurniawan carries a reputational and
 * legal check before any invitation (spec §5). Enforced by the API, not just the UI.
 */
export const VERAFIED_GUEST_STATUSES_ALLOWED_UNDER_CHECK: readonly VerafiedGuestStatus[] = ['not_invited', 'paused'];

export const VERAFIED_INFLUENCE = ['high', 'medium', 'low'] as const;
export type VerafiedInfluence = (typeof VERAFIED_INFLUENCE)[number];
export const VERAFIED_INFLUENCE_LABELS: Readonly<Record<VerafiedInfluence, string>> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** How we know them. */
export const VERAFIED_CONNECTIONS = ['first', 'second', 'unknown'] as const;
export type VerafiedConnection = (typeof VERAFIED_CONNECTIONS)[number];
export const VERAFIED_CONNECTION_LABELS: Readonly<Record<VerafiedConnection, string>> = {
  first: '1st degree',
  second: '2nd degree',
  unknown: 'Unknown',
};

/** Typed profile links. Several are allowed per guest. */
export const VERAFIED_LINK_KINDS = ['linkedin', 'instagram', 'website', 'wikipedia', 'imdb'] as const;
export type VerafiedLinkKind = (typeof VERAFIED_LINK_KINDS)[number];
export const VERAFIED_LINK_KIND_LABELS: Readonly<Record<VerafiedLinkKind, string>> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  website: 'Website',
  wikipedia: 'Wikipedia',
  imdb: 'IMDb',
};

/** Classify a profile URL by host. Anything unrecognised is a `website`. Pure, so it is guarded. */
export const verafiedLinkKind = (url: string): VerafiedLinkKind => {
  // A regex, not `new URL()`: this package compiles without the DOM lib.
  const m = /^https?:\/\/([^/?#:]+)/i.exec(url.trim());
  if (!m) return 'website';
  const host = m[1].toLowerCase().replace(/^www\./, '');
  if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) return 'linkedin';
  if (host === 'instagram.com' || host.endsWith('.instagram.com')) return 'instagram';
  if (host === 'wikipedia.org' || host.endsWith('.wikipedia.org')) return 'wikipedia';
  if (host === 'imdb.com' || host.endsWith('.imdb.com')) return 'imdb';
  return 'website';
};

/** The state of ONE guest↔episode link (spec §1: each link has its own state). */
export const VERAFIED_LINK_STATES = ['candidate', 'invited', 'confirmed', 'recorded'] as const;
export type VerafiedLinkState = (typeof VERAFIED_LINK_STATES)[number];
export const VERAFIED_LINK_STATE_LABELS: Readonly<Record<VerafiedLinkState, string>> = {
  candidate: 'Candidate',
  invited: 'Invited',
  confirmed: 'Confirmed',
  recorded: 'Recorded',
};

/** The editorial verdict on an episode idea. NULL = unrated. */
export const VERAFIED_EVALUATIONS = ['love', 'like', 'unsure'] as const;
export type VerafiedEvaluation = (typeof VERAFIED_EVALUATIONS)[number];
export const VERAFIED_EVALUATION_LABELS: Readonly<Record<VerafiedEvaluation, string>> = {
  love: 'Love it',
  like: 'Like it',
  unsure: 'Unsure',
};

/** The episode pipeline, in board order. `shelved` is the collapsible strip at the end. */
export const VERAFIED_EPISODE_STATUSES = [
  'idea',
  'approved',
  'guest_invited',
  'guest_confirmed',
  'scheduled',
  'recorded',
  'in_edit',
  'ready',
  'published',
  'shelved',
] as const;
export type VerafiedEpisodeStatus = (typeof VERAFIED_EPISODE_STATUSES)[number];

export const VERAFIED_EPISODE_STATUS_LABELS: Readonly<Record<VerafiedEpisodeStatus, string>> = {
  idea: 'Idea',
  approved: 'Approved',
  guest_invited: 'Guest invited',
  guest_confirmed: 'Guest confirmed',
  scheduled: 'Scheduled',
  recorded: 'Recorded',
  in_edit: 'In edit',
  ready: 'Ready',
  published: 'Published',
  shelved: 'Shelved',
};

const includes =
  <T extends string>(list: readonly T[]) =>
  (v: unknown): v is T =>
    typeof v === 'string' && (list as readonly string[]).includes(v);

export const isVerafiedGuestType = includes(VERAFIED_GUEST_TYPES);
export const isVerafiedGuestStatus = includes(VERAFIED_GUEST_STATUSES);
export const isVerafiedInfluence = includes(VERAFIED_INFLUENCE);
export const isVerafiedConnection = includes(VERAFIED_CONNECTIONS);
export const isVerafiedLinkKind = includes(VERAFIED_LINK_KINDS);
export const isVerafiedLinkState = includes(VERAFIED_LINK_STATES);
export const isVerafiedEvaluation = includes(VERAFIED_EVALUATIONS);
export const isVerafiedEpisodeStatus = includes(VERAFIED_EPISODE_STATUSES);
